import React from 'react';
import { Button } from './ui/button';
import { ArrowRight, Sparkles, Zap } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { useTranslation } from 'react-i18next';

const Hero = () => {
  const { isDark } = useTheme();
  const { t } = useTranslation();
  
  const scrollToCustomizer = () => {
    const customizerSection = document.getElementById('customizer');
    if (customizerSection) {
      customizerSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div 
      className="relative min-h-screen flex items-center justify-center overflow-hidden transition-colors duration-300"
      style={{ 
        backgroundColor: isDark ? '#000000' : '#ffffff'
      }}
    >
      {/* Animated background gradient */}
      <div 
        className="absolute inset-0 transition-opacity duration-300"
        style={{
          background: isDark 
            ? 'radial-gradient(circle at 20% 50%, rgba(236, 72, 153, 0.15) 0%, transparent 50%), radial-gradient(circle at 80% 50%, rgba(59, 130, 246, 0.15) 0%, transparent 50%), #000000'
            : 'linear-gradient(135deg, #ffffff 0%, #fce7f3 50%, #ffffff 100%)'
        }}
      />
      
      {/* Decorative neon elements */}
      <div 
        className="absolute top-20 left-10 w-72 h-72 rounded-full blur-3xl animate-pulse"
        style={{
          backgroundColor: isDark ? 'rgba(255, 0, 255, 0.2)' : 'rgba(236, 72, 153, 0.15)',
          boxShadow: isDark ? '0 0 150px rgba(255, 0, 255, 0.4)' : '0 0 80px rgba(236, 72, 153, 0.2)'
        }}
      />
      <div 
        className="absolute bottom-20 right-10 w-96 h-96 rounded-full blur-3xl animate-pulse"
        style={{ 
          animationDelay: '1s',
          backgroundColor: isDark ? 'rgba(0, 255, 255, 0.2)' : 'rgba(59, 130, 246, 0.15)',
          boxShadow: isDark ? '0 0 150px rgba(0, 255, 255, 0.4)' : '0 0 80px rgba(59, 130, 246, 0.2)'
        }}
      />
      <div 
        className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-64 h-64 rounded-full blur-3xl animate-pulse"
        style={{ 
          animationDelay: '2s',
          backgroundColor: isDark ? 'rgba(255, 255, 0, 0.15)' : 'rgba(168, 85, 247, 0.1)',
          boxShadow: isDark ? '0 0 120px rgba(255, 255, 0, 0.3)' : '0 0 60px rgba(168, 85, 247, 0.2)'
        }}
      />
      
      <div className="relative z-10 max-w-7xl mx-auto px-3 md:px-4 py-12 md:py-20 text-center">
        {/* Badge */}
        <div 
          className="inline-flex items-center gap-1 md:gap-2 px-3 md:px-4 py-1.5 md:py-2 rounded-full border backdrop-blur-sm mb-4 md:mb-8 transition-colors duration-300"
          style={{
            backgroundColor: isDark ? 'rgba(20, 20, 20, 0.8)' : 'rgba(255, 255, 255, 0.9)',
            borderColor: isDark ? '#ff00ff' : '#ff1493',
            borderWidth: '2px',
            boxShadow: isDark ? '0 0 20px rgba(255, 0, 255, 0.3)' : '0 0 20px rgba(255, 20, 147, 0.4)'
          }}
        >
          <Sparkles className="w-3 h-3 md:w-4 md:h-4" style={{ color: isDark ? '#ff00ff' : '#ff1493' }} />
          <span 
            className="text-xs md:text-sm transition-colors duration-300 font-semibold"
            style={{ 
              color: isDark ? '#00ffff' : '#ff00ff',
              textShadow: isDark ? '0 0 10px rgba(0, 255, 255, 0.8)' : '0 0 10px rgba(255, 0, 255, 0.6)'
            }}
          >
            {t('hero.badge')}
          </span>
        </div>

        {/* Main heading with vibrant neon glow */}
        <h1 className="text-4xl sm:text-6xl md:text-8xl lg:text-9xl font-bold mb-4 md:mb-6 tracking-tight">
          <span 
            className="transition-colors duration-300 font-extrabold"
            style={{ 
              color: isDark ? '#ffffff' : '#0f172a',
              textShadow: isDark ? '0 0 20px rgba(255, 255, 255, 0.8), 0 0 40px rgba(255, 255, 255, 0.5)' : '0 0 15px rgba(15, 23, 42, 0.3)'
            }}
          >
            {t('hero.title1')}
          </span>
          <br />
          <span 
            className={`font-extrabold relative inline-block ${isDark ? 'gradient-text-hero-dark' : 'gradient-text-hero-light'}`}
            style={{
              filter: isDark 
                ? 'drop-shadow(0 0 20px rgba(255, 0, 255, 0.8)) drop-shadow(0 0 40px rgba(0, 255, 255, 0.6))'
                : 'drop-shadow(0 0 15px rgba(255, 20, 147, 0.7)) drop-shadow(0 0 30px rgba(0, 191, 255, 0.5))'
            }}
          >
            {t('hero.title2')}
          </span>
        </h1>

        <p 
          className="text-base sm:text-lg md:text-2xl mb-8 md:mb-12 max-w-3xl mx-auto leading-relaxed transition-colors duration-300 font-medium px-2"
          style={{ 
            color: isDark ? '#00ff88' : '#9333ea',
            textShadow: isDark ? '0 0 15px rgba(0, 255, 136, 0.6)' : '0 0 15px rgba(147, 51, 234, 0.4)',
            fontWeight: '600'
          }}
        >
          {t('hero.subtitle')}
        </p>

        {/* CTA Buttons */}
        <div className="flex flex-col sm:flex-row gap-3 md:gap-4 justify-center items-center mb-10 md:mb-16">
          <Button 
            size="lg"
            onClick={scrollToCustomizer}
            className="text-white px-6 py-4 md:px-8 md:py-6 text-base md:text-lg group transition-all duration-300 hover:scale-105 shadow-lg font-bold"
            style={{
              background: isDark 
                ? 'linear-gradient(135deg, #ff00ff 0%, #ff0080 100%)'
                : 'linear-gradient(135deg, #ff1493 0%, #ff0080 100%)',
              boxShadow: isDark 
                ? '0 10px 60px rgba(255, 0, 255, 0.6), 0 0 40px rgba(255, 0, 255, 0.4)'
                : '0 10px 50px rgba(255, 20, 147, 0.6), 0 0 30px rgba(255, 0, 128, 0.5)'
            }}
          >
            {t('hero.ctaNeon')}
            <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </Button>
          <Button 
            size="lg"
            variant="outline"
            className="px-6 py-4 md:px-8 md:py-6 text-base md:text-lg backdrop-blur-sm transition-all duration-300 font-bold"
            style={{
              borderColor: isDark ? '#00ffff' : '#00bfff',
              borderWidth: '2px',
              backgroundColor: isDark ? 'rgba(0, 0, 0, 0.5)' : 'rgba(255, 255, 255, 0.8)',
              color: isDark ? '#00ffff' : '#00bfff',
              textShadow: isDark ? '0 0 10px rgba(0, 255, 255, 0.8)' : '0 0 10px rgba(0, 191, 255, 0.6)',
              boxShadow: isDark ? '0 0 30px rgba(0, 255, 255, 0.3)' : '0 0 25px rgba(0, 191, 255, 0.4)'
            }}
            onClick={() => {
              const quoteSection = document.getElementById('quote');
              if (quoteSection) quoteSection.scrollIntoView({ behavior: 'smooth' });
            }}
          >
            {t('hero.ctaQuote')}
          </Button>
        </div>

        {/* Feature highlights with neon accents */}
        <div className="grid grid-cols-3 gap-2 md:gap-6 max-w-4xl mx-auto">
          <div 
            className="p-3 md:p-6 rounded-xl md:rounded-2xl border backdrop-blur-sm transition-all duration-300 hover:scale-105"
            style={{
              backgroundColor: isDark ? 'rgba(20, 20, 20, 0.8)' : 'rgba(255, 255, 255, 0.9)',
              borderColor: isDark ? '#ff1493' : '#ff1493',
              borderWidth: '2px',
              boxShadow: isDark ? '0 0 30px rgba(255, 20, 147, 0.3)' : '0 0 25px rgba(255, 20, 147, 0.35)'
            }}
          >
            <div 
              className="w-8 h-8 md:w-12 md:h-12 rounded-full flex items-center justify-center mb-2 md:mb-4 mx-auto"
              style={{
                background: isDark 
                  ? 'linear-gradient(135deg, rgba(255, 20, 147, 0.3) 0%, rgba(255, 0, 255, 0.3) 100%)'
                  : 'linear-gradient(135deg, rgba(255, 20, 147, 0.25) 0%, rgba(255, 0, 255, 0.25) 100%)',
                boxShadow: isDark 
                  ? '0 0 40px rgba(255, 20, 147, 0.6)'
                  : '0 0 35px rgba(255, 20, 147, 0.5)'
              }}
            >
              <Zap className="w-4 h-4 md:w-6 md:h-6" style={{ color: '#ff1493' }} />
            </div>
            <h3 
              className="font-semibold mb-1 md:mb-2 transition-colors duration-300 text-xs md:text-base"
              style={{ 
                color: isDark ? '#ff1493' : '#ff1493',
                textShadow: isDark ? '0 0 15px rgba(255, 20, 147, 0.8)' : '0 0 12px rgba(255, 20, 147, 0.6)'
              }}
            >
              {t('hero.categories')}
            </h3>
            <p 
              className="text-xs md:text-sm transition-colors duration-300 hidden md:block"
              style={{ color: isDark ? '#ffffff' : '#334155' }}
            >
              {t('hero.categoriesDesc')}
            </p>
          </div>

          <div 
            className="p-3 md:p-6 rounded-xl md:rounded-2xl border backdrop-blur-sm transition-all duration-300 hover:scale-105"
            style={{
              backgroundColor: isDark ? 'rgba(20, 20, 20, 0.8)' : 'rgba(255, 255, 255, 0.9)',
              borderColor: isDark ? '#00ffff' : '#00bfff',
              borderWidth: '2px',
              boxShadow: isDark ? '0 0 30px rgba(0, 255, 255, 0.3)' : '0 0 25px rgba(0, 191, 255, 0.35)'
            }}
          >
            <div 
              className="w-8 h-8 md:w-12 md:h-12 rounded-full flex items-center justify-center mb-2 md:mb-4 mx-auto"
              style={{
                background: isDark 
                  ? 'linear-gradient(135deg, rgba(0, 255, 255, 0.3) 0%, rgba(0, 191, 255, 0.3) 100%)'
                  : 'linear-gradient(135deg, rgba(0, 191, 255, 0.25) 0%, rgba(0, 255, 255, 0.25) 100%)',
                boxShadow: isDark 
                  ? '0 0 40px rgba(0, 255, 255, 0.6)'
                  : '0 0 35px rgba(0, 191, 255, 0.5)'
              }}
            >
              <Sparkles className="w-4 h-4 md:w-6 md:h-6" style={{ color: isDark ? '#00ffff' : '#00bfff' }} />
            </div>
            <h3 
              className="font-semibold mb-1 md:mb-2 transition-colors duration-300 text-xs md:text-base"
              style={{ 
                color: isDark ? '#00ffff' : '#00bfff',
                textShadow: isDark ? '0 0 15px rgba(0, 255, 255, 0.8)' : '0 0 12px rgba(0, 191, 255, 0.6)'
              }}
            >
              {t('hero.customDesign')}
            </h3>
            <p 
              className="text-xs md:text-sm transition-colors duration-300 hidden md:block"
              style={{ color: isDark ? '#ffffff' : '#334155' }}
            >
              {t('hero.customDesignDesc')}
            </p>
          </div>

          <div 
            className="p-3 md:p-6 rounded-xl md:rounded-2xl border backdrop-blur-sm transition-all duration-300 hover:scale-105"
            style={{
              backgroundColor: isDark ? 'rgba(20, 20, 20, 0.8)' : 'rgba(255, 255, 255, 0.9)',
              borderColor: isDark ? '#ffff00' : '#ffd700',
              borderWidth: '2px',
              boxShadow: isDark ? '0 0 30px rgba(255, 255, 0, 0.3)' : '0 0 25px rgba(255, 215, 0, 0.4)'
            }}
          >
            <div 
              className="w-8 h-8 md:w-12 md:h-12 rounded-full flex items-center justify-center mb-2 md:mb-4 mx-auto"
              style={{
                background: isDark 
                  ? 'linear-gradient(135deg, rgba(255, 255, 0, 0.3) 0%, rgba(255, 215, 0, 0.3) 100%)'
                  : 'linear-gradient(135deg, rgba(255, 215, 0, 0.25) 0%, rgba(255, 255, 0, 0.25) 100%)',
                boxShadow: isDark 
                  ? '0 0 40px rgba(255, 255, 0, 0.6)'
                  : '0 0 35px rgba(255, 215, 0, 0.5)'
              }}
            >
              <svg className="w-4 h-4 md:w-6 md:h-6" style={{ color: isDark ? '#ffff00' : '#ffa500' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
            </div>
            <h3 
              className="font-semibold mb-1 md:mb-2 transition-colors duration-300 text-xs md:text-base"
              style={{ 
                color: isDark ? '#ffff00' : '#ff8c00',
                textShadow: isDark ? '0 0 15px rgba(255, 255, 0, 0.8)' : '0 0 12px rgba(255, 140, 0, 0.6)'
              }}
            >
              {t('hero.warranty')}
            </h3>
            <p 
              className="text-xs md:text-sm transition-colors duration-300 hidden md:block"
              style={{ color: isDark ? '#ffffff' : '#334155' }}
            >
              {t('hero.warrantyDesc')}
            </p>
          </div>
        </div>
      </div>

      {/* Scroll indicator - hide on mobile */}
      <div className="absolute bottom-4 md:bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce hidden sm:block">
        <div 
          className="w-6 h-10 border-2 rounded-full flex justify-center transition-colors duration-300"
          style={{ borderColor: isDark ? '#00ff88' : '#cbd5e1' }}
        >
          <div 
            className="w-1 h-3 rounded-full mt-2 transition-colors duration-300"
            style={{ backgroundColor: isDark ? '#00ff88' : '#94a3b8' }}
          />
        </div>
      </div>
    </div>
  );
};

export default Hero;
